import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { FeedItem } from '../../types';
import ReactionBar from '../shared/ReactionBar';
import CommentsModal from '../shared/CommentsModal';
import './FeedCard.css';

interface PhotoPostCardProps {
  item: FeedItem;
  currentUserId: number;
  onDelete?: (postId: number) => void;
}

const PhotoPostCard: React.FC<PhotoPostCardProps> = ({ item, currentUserId, onDelete }) => {
  const { t } = useLanguage();
  const [showComments, setShowComments] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const isOwner = item.user_id === currentUserId;

  useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMenu]);

  const timeAgo = (timestamp: string) => {
    const diff = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (diff < 60) return t('feed.timeAgo.now');
    if (diff < 3600) return t('feed.timeAgo.minutes').replace('{{count}}', String(Math.floor(diff / 60)));
    if (diff < 86400) return t('feed.timeAgo.hours').replace('{{count}}', String(Math.floor(diff / 3600)));
    return t('feed.timeAgo.days').replace('{{count}}', String(Math.floor(diff / 86400)));
  };

  return (
    <div className="feed-card">
      <div className="feed-card-header">
        {item.user_profile_picture ? (
          <img src={item.user_profile_picture} alt="" className="feed-card-avatar" />
        ) : (
          <div className="feed-card-avatar-placeholder">
            {item.user_first_name?.[0] || '?'}
          </div>
        )}
        <div className="feed-card-user-info">
          <div className="feed-card-user-name">{item.user_first_name} {item.user_last_name}</div>
          <div className="feed-card-meta">
            <span>{timeAgo(item.timestamp)}</span>
            <span>·</span>
            <span>📷 {t('feed.photoPost')}</span>
          </div>
        </div>
        {isOwner && (
          <div className="feed-card-menu-wrapper" ref={menuRef}>
            <button className="feed-card-menu-btn" onClick={() => setShowMenu(v => !v)}>&#8943;</button>
            {showMenu && (
              <div className="feed-card-menu">
                <button className="feed-card-menu-item feed-card-menu-item--danger" onClick={() => {
                  setShowMenu(false);
                  onDelete?.(item.id);
                }}>
                  {t('feed.menu.delete')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {item.text_content && (
        <div style={{ padding: '0 16px 8px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          {item.text_content}
        </div>
      )}

      {item.image_url && (
        <img
          src={item.image_url}
          alt=""
          style={{ width: '100%', maxHeight: '400px', objectFit: 'cover' }}
          loading="lazy"
        />
      )}

      <div className="feed-card-reactions">
        <ReactionBar
          targetType="feed_post"
          targetId={item.id}
          clubupCount={item.clubup_count}
          assCount={item.ass_count}
          hasClubup={item.has_clubup}
          hasAss={item.has_ass}
          commentCount={item.comment_count}
          recentReactors={item.recent_reactors}
          aiComment={item.ai_comment}
          onCommentClick={() => setShowComments(true)}
        />
      </div>

      {showComments && (
        <CommentsModal
          targetType="feed_post"
          targetId={item.id}
          currentUserId={currentUserId}
          onClose={() => setShowComments(false)}
        />
      )}
    </div>
  );
};

export default PhotoPostCard;
